import React from "react";
import logoMombasa from "../../../core/img/branch-logos/logo_mombasa.jpg";
import logoNairobi from "../../../core/img/branch-logos/logo_nairobi.jpg";
import logoSystems from "../../../core/img/branch-logos/logo_systems.jpg";
import logoTexastrack from "../../../core/img/branch-logos/logo_texastrack.png";

// Printable AMC register (2026-09-09). Prints exactly the rows the screen is
// showing - the same filter and search the user already applied - so what comes
// out of the printer matches what they were looking at.
//
// Inline styles throughout: this is also rendered via
// ReactDOMServer.renderToStaticMarkup into an about:blank popup that has no
// stylesheet, the same one-template-two-render-paths approach as the petty cash
// voucher and the stock documents. Logos resolve to absolute URLs for the same
// reason - a webpack root-relative path has no base to resolve against there.
const LOGO_BY_BRANCH = {
  MOMBASA: logoMombasa,
  NAIROBI: logoNairobi,
  SYSTEMS: logoSystems,
  TEXASTRACK: logoTexastrack,
};

const logoUrlFor = (branchCode) =>
  new URL(LOGO_BY_BRANCH[branchCode] || logoMombasa, window.location.origin).href;

const money = (v) =>
  v == null ? "-" : Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmt = (v) => (v ? String(v).substring(0, 10) : "-");

// Balance is derived exactly as the screen derives it (Batch154) - never stored -
// so paper and screen can't disagree. Worded the same way too.
const balanceText = (r) => {
  if (r.amcAmount == null) return "-";
  const balance = Number(r.amcAmount) - (Number(r.paidAmount) || 0);
  if (balance === 0) return "settled";
  if (balance < 0) return `+${money(Math.abs(balance))} over`;
  return money(balance);
};

const AmcRegisterPrint = ({ rows = [], filterLabel, customerName, dateLabel, searchTerm }) => {
  // Running total accumulates down the printed order, so any row can be read as
  // "everything down to here". A contract with no amount recorded contributes
  // nothing rather than breaking the running figure.
  let running = 0;
  const priced = rows.map((r) => {
    running += Number(r.amcAmount) || 0;
    return { ...r, runningTotal: running };
  });
  const grandTotal = running;
  const paidTotal = rows.reduce((s, r) => s + (Number(r.paidAmount) || 0), 0);
  const balanceTotal = rows.reduce((s, r) => s + ((Number(r.amcAmount) || 0) - (Number(r.paidAmount) || 0)), 0);
  const missingAmount = rows.filter((r) => r.amcAmount == null).length;
  const notInvoiced = rows.filter((r) => !r.invoiceNumber).length;

  const th = {
    border: "0.7px solid #000", padding: "4px 6px", fontSize: 10,
    background: "#f1f1f1", textAlign: "left", fontWeight: 700,
  };
  const td = { border: "0.7px solid #000", padding: "4px 6px", fontSize: 10, verticalAlign: "top" };
  const num = { ...td, textAlign: "right", whiteSpace: "nowrap" };

  return (
    <div style={{ fontFamily: "Arial, Helvetica, sans-serif", color: "#000", background: "#fff", padding: 16 }}>
      <div style={{ textAlign: "center" }}>
        <img src={logoUrlFor(rows[0]?.branchCode)} alt="" style={{ height: 62, objectFit: "contain" }} />
      </div>

      <div style={{ textAlign: "center", marginTop: 8 }}>
        <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: 1 }}>AMC REGISTER</div>
        <div style={{ fontSize: 10, color: "#444", marginTop: 2 }}>
          {customerName ? `${customerName} · ` : ""}
          {filterLabel ? `${filterLabel} · ` : ""}
          {dateLabel ? `${dateLabel} · ` : ""}
          {searchTerm ? `matching "${searchTerm}" · ` : ""}
          {rows.length} contract{rows.length === 1 ? "" : "s"} · printed{" "}
          {new Date().toLocaleString("en-GB")}
        </div>
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 12 }}>
        <thead>
          <tr>
            <th style={{ ...th, width: 26 }}>#</th>
            <th style={th}>Contract</th>
            <th style={th}>Customer</th>
            <th style={th}>Period</th>
            <th style={th}>Every</th>
            <th style={th}>Visits</th>
            <th style={th}>Status</th>
            <th style={{ ...th, textAlign: "right" }}>Amount</th>
            <th style={{ ...th, textAlign: "right" }}>Running Total</th>
            {/* Batch154 billing columns - on screen since 10-Sep, missing from
                the printout until 2026-09-11. */}
            <th style={th}>Invoice</th>
            <th style={{ ...th, textAlign: "right" }}>Paid</th>
            <th style={{ ...th, textAlign: "right" }}>Balance</th>
          </tr>
        </thead>
        <tbody>
          {priced.map((r, i) => (
            <tr key={r.recordGuid}>
              <td style={td}>{i + 1}</td>
              <td style={td}>
                <strong>{r.contractNo}</strong>
                <div style={{ color: "#555" }}>{r.branchCode}</div>
              </td>
              <td style={td}>
                {r.customerName || `Customer ${r.custId}`}
                {r.serviceTypeName && <div style={{ color: "#555" }}>AMC for: {r.serviceTypeName}</div>}
              </td>
              <td style={td}>{fmt(r.startDate)} to {fmt(r.endDate)}</td>
              <td style={td}>{r.frequency?.replace(/_/g, " ").toLowerCase()}</td>
              <td style={td}>{r.visitsDone}/{r.visitCount}</td>
              <td style={td}>{r.effectiveStatus?.replace(/_/g, " ")}</td>
              <td style={num}>{money(r.amcAmount)}</td>
              <td style={num}>{money(r.runningTotal)}</td>
              <td style={td}>{r.invoiceNumber || <span style={{ color: "#777" }}>not invoiced</span>}</td>
              <td style={num}>{money(r.paidAmount)}</td>
              <td style={num}>{balanceText(r)}</td>
            </tr>
          ))}
          {priced.length === 0 && (
            <tr>
              <td style={td} colSpan={12}>Nothing to print for this filter.</td>
            </tr>
          )}
        </tbody>
        {priced.length > 0 && (
          <tfoot>
            <tr>
              <td style={{ ...td, fontWeight: 700 }} colSpan={7}>Total</td>
              <td style={{ ...num, fontWeight: 700 }}>{money(grandTotal)}</td>
              <td style={{ ...num, fontWeight: 700 }}>{money(grandTotal)}</td>
              <td style={td} />
              <td style={{ ...num, fontWeight: 700 }}>{money(paidTotal)}</td>
              <td style={{ ...num, fontWeight: 700 }}>{money(balanceTotal)}</td>
            </tr>
          </tfoot>
        )}
      </table>

      {/* Said plainly rather than left for someone to notice the total looks
          low - a contract with no amount recorded is invisible in a money
          column otherwise. */}
      {missingAmount > 0 && (
        <div style={{ marginTop: 8, fontSize: 9.5, color: "#8a6d1f" }}>
          {missingAmount} contract{missingAmount === 1 ? " has" : "s have"} no AMC amount recorded and contribute
          nothing to the total.
        </div>
      )}
      {notInvoiced > 0 && (
        <div style={{ marginTop: 4, fontSize: 9.5, color: "#555" }}>
          {notInvoiced} contract{notInvoiced === 1 ? " is" : "s are"} not invoiced yet.
        </div>
      )}
    </div>
  );
};

export default AmcRegisterPrint;
